import { Dispatch } from 'react';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatRelative } from 'date-fns';
import { toast } from 'sonner';
import { IUser } from '@/redux/auth/auth';
import { ICarouselPage } from '@/interfaces/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function objectToFormData(values: Record<string, any>): FormData {
  const formData = new FormData();

  Object.entries(values).forEach(([key, value]) => {
    // Skip undefined values
    if (value === undefined) {
      return;
    }

    if (value && value instanceof File) {
      formData.append(key, value);
    } else if (
      Array.isArray(value) &&
      value.every((item) => item instanceof File)
    ) {
      value.forEach((file: File) => {
        formData.append(key, file);
      });
    } else if (Array.isArray(value)) {
      // Check if the value is an array of objects and stringify them
      value.forEach((item: any) => {
        // If item is an object, stringify it, otherwise just append the item
        formData.append(
          key,
          typeof item === 'object' ? JSON.stringify(item) : item
        );
      });
    } else {
      formData.append(
        key,
        typeof value === 'object' ? JSON.stringify(value) : value
      );
    }
  });

  return formData;
}

export function getRelativeTime(date: Date | string | undefined | null) {
  if (!date) {
    return 'Unknown date';
  }

  const currentDate = new Date();
  const parsedDate = new Date(date);

  // Check if the date is valid
  if (Number.isNaN(parsedDate.getTime())) {
    return 'Invalid date';
  }

  return formatRelative(parsedDate, currentDate);
}

export function getUserFullName(user: IUser | undefined) {
  if (!user) return '';

  if (user.firstname || user.lastname) {
    return [user.firstname, user.lastname].filter(Boolean).join(' ');
  }

  return user.username;
}

/**
 * Returns a display name for a user-like object. Falls back to username
 * when firstname/lastname are hidden (undefined).
 */
export function getDisplayName(
  // Makes outlive their uploader, so the author can genuinely be absent.
  user:
    | {
        firstname?: string;
        lastname?: string;
        username?: string;
      }
    | undefined
    | null
): string {
  if (!user) return '';
  if (user.firstname || user.lastname) {
    return [user.firstname, user.lastname].filter(Boolean).join(' ');
  }
  return user.username || '';
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const currentDate = new Date();

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
  };
  const formattedDate = date.toLocaleDateString('en-US', options);

  if (date.getFullYear() === currentDate.getFullYear()) {
    return formattedDate;
  }
  return `${date.getFullYear().toString().slice(-2)} ${formattedDate}`;
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['week', 7 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
];

/** "3 hours ago". Past a week the absolute date reads better, so fall back. */
export function formatRelativeTime(timestamp: string): string {
  const seconds = Math.round(
    (Date.now() - new Date(timestamp).getTime()) / 1000
  );
  if (seconds >= 7 * 24 * 60 * 60) return formatTimestamp(timestamp);
  if (seconds < 60) return 'just now';

  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const match = RELATIVE_UNITS.find(([, size]) => seconds >= size);
  if (!match) return 'just now';
  const [unit, size] = match;
  return formatter.format(-Math.floor(seconds / size), unit);
}

export async function validateStep<T>(
  currentStep: number,
  pages: ICarouselPage[],
  formData: T,
  setErrors: Dispatch<{ [key: string]: string }>
) {
  const currentPage = pages[currentStep - 1];

  // Skip validation if no schema is defined
  if (!currentPage.schema) {
    return { success: true };
  }

  const result = await currentPage.schema.safeParseAsync(formData);

  if (result.success) {
    return { success: true };
  }

  const newErrors: { [key: string]: string } = {};
  const errorMessages: string[] = [];

  result.error.errors.forEach((err: any) => {
    newErrors[err.path[0]] = err.message;
    errorMessages.push(err.message);
  });

  setErrors(newErrors);

  // Show toast notifications for each error
  if (errorMessages.length > 0) {
    // If there's only one error, show it directly
    if (errorMessages.length === 1) {
      toast.error('Validation Error', {
        description: errorMessages[0],
      });
    } else {
      // If there are multiple errors, show them as a formatted string
      const errorList = errorMessages.map((msg) => `• ${msg}`).join('\n');
      toast.error('Validation Errors', {
        description: errorList,
      });
    }
  }

  return { success: false, errors: newErrors };
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
  // eslint-disable-next-line no-undef
  let timeout: NodeJS.Timeout | null = null;
  const debounced = (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
  debounced.cancel = () => {
    if (timeout) clearTimeout(timeout);
    timeout = null;
  };
  return debounced;
}

export function sortByOnefinity<T>(
  arr: T[],
  getValue: (item: T) => string = (item) => String(item)
): T[] {
  const onefinityItems: T[] = [];
  const otherItems: T[] = [];
  arr.forEach((item) => {
    const value = getValue(item).toLowerCase();
    if (value.startsWith('onefinity')) {
      onefinityItems.push(item);
    } else {
      otherItems.push(item);
    }
  });
  return [...onefinityItems, ...otherItems];
}

export type GroupedMachine = {
  brand: string;
  model: string;
  brandLink?: string;
  modelLink?: string;
  modelPicture?: string;
  variants: Array<{
    id: string;
    name: string;
    link?: string;
  }>;
};

export const groupMachinesByBrandAndModel = <
  T extends {
    brand: string;
    model: string;
    brandLink?: string;
    modelLink?: string;
    modelPicture?: string;
    variant: {
      id: string;
      name: string;
      link?: string;
    };
  },
>(
  machines: T[]
): GroupedMachine[] => {
  const groupedMap = new Map<string, GroupedMachine>();

  machines.forEach((machine) => {
    const key = `${machine.brand}-${machine.model}`;

    if (!groupedMap.has(key)) {
      groupedMap.set(key, {
        brand: machine.brand,
        model: machine.model,
        brandLink: machine.brandLink,
        modelLink: machine.modelLink,
        modelPicture: machine.modelPicture,
        variants: [],
      });
    }

    const group = groupedMap.get(key)!;
    group.variants.push(machine.variant);
  });

  return Array.from(groupedMap.values());
};
